/**
 * CookieConsentBanner — Iter 67am.
 *
 * Slide-up bottom banner shown to first-time visitors until they make a
 * choice (Accept all / Essentials only). Choice is persisted in
 * localStorage under `rentalcar:cookie-consent` so the banner doesn't
 * pester returning visitors.
 *
 * Banner copy is admin-managed via Admin → Site Settings → "Cookie banner"
 * HTML field. When the field is empty we fall back to a sensible default so
 * the banner stays GDPR-presentable from day one.
 *
 * The banner is rendered globally (mounted once in App.js); each page-level
 * Footer "Cookies" link can also re-open it via `window.dispatchEvent(new
 * CustomEvent("rentalcar:open-cookie-banner"))`.
 */
import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { isEmbedSurface } from "@/lib/isEmbedSurface";

const STORAGE_KEY = "rentalcar:cookie-consent";
// Iter 67an — exit-intent re-engagement: if a user clicks "Essentials only"
// and leaves within this many ms, we flag the next visit to show a small
// "Tip: enable analytics for better deals" hint near the footer Cookies link.
const QUICK_EXIT_THRESHOLD_MS = 30_000;
const REENGAGE_KEY = "rentalcar:cookie-reengage";

const DEFAULT_COPY = `<p>We use cookies to keep you signed in, remember your preferences, and measure how the site is used. You can accept all cookies or just the essential ones — change your mind anytime via the link in our footer.</p>`;

function _getStored() {
  if (typeof window === "undefined") return null;
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null"); }
  catch { return null; }
}

function _store(decision) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      decision, decided_at: new Date().toISOString(),
    }));
  } catch { /* ignore quota / private mode */ }
}

/**
 * Iter 67an — exit-intent tracker. Wired in `decide()` only when the user
 * picks "essentials" (we don't bug the "all" or "dismissed" cohorts). If
 * `pagehide` / `beforeunload` fires within 30s of the decision we set the
 * `pending` flag; the next mount of the banner picks it up and arms the
 * footer-tip nudge. We deliberately do NOT show the nudge on the SAME
 * session — leaving and coming back is the trigger.
 */
function _armExitIntent() {
  if (typeof window === "undefined") return () => {};
  const armedAt = Date.now();
  const onLeave = () => {
    const elapsed = Date.now() - armedAt;
    if (elapsed > QUICK_EXIT_THRESHOLD_MS) return;
    try {
      window.localStorage.setItem(REENGAGE_KEY, JSON.stringify({
        pending: true, armed_at: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
  };
  // `pagehide` is more reliable than `beforeunload` on iOS Safari + browsers
  // freezing tabs. Listen to both for completeness.
  window.addEventListener("pagehide", onLeave);
  window.addEventListener("beforeunload", onLeave);
  return () => {
    window.removeEventListener("pagehide", onLeave);
    window.removeEventListener("beforeunload", onLeave);
  };
}

export default function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [copyHtml, setCopyHtml] = useState(DEFAULT_COPY);

  // Decide whether to show on first paint, and listen for the "open" event
  // dispatched from the footer Cookies link.
  useEffect(() => {
    // Iter 69 — never render inside `/embed/*` or any iframe. Partner sites
    // already have their own consent banner; we'd otherwise stack two.
    if (isEmbedSurface()) return undefined;
    const stored = _getStored();
    if (!stored?.decision) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener("rentalcar:open-cookie-banner", reopen);
    return () => window.removeEventListener("rentalcar:open-cookie-banner", reopen);
  }, []);

  // Fetch admin-authored copy (only if we're going to show the banner).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/site-settings")
      .then((r) => {
        if (cancelled) return;
        const html = (r.data?.cookie_html || "").trim();
        setCopyHtml(html || DEFAULT_COPY);
      })
      .catch(() => { if (!cancelled) setCopyHtml(DEFAULT_COPY); });
    return () => { cancelled = true; };
  }, [open]);

  const decide = (decision) => {
    _store(decision);
    setOpen(false);
    // Iter 67an — only arm the exit-intent watcher for "essentials" picks;
    // we don't pester "Accept all" cohorts. The watcher is intentionally
    // page-scoped: if the user navigates around in the same session it
    // resets, which is exactly the "they engaged" signal we want.
    if (decision === "essentials" && typeof window !== "undefined") {
      const cleanup = _armExitIntent();
      // Auto-disarm after the threshold so we don't keep listeners forever.
      window.setTimeout(cleanup, QUICK_EXIT_THRESHOLD_MS + 500);
    }
  };

  if (!open) return null;

  // Iter 67at — DOMPurify defence-in-depth before injecting admin-authored HTML.
  const sanitizedCopy = DOMPurify.sanitize(copyHtml || DEFAULT_COPY, { USE_PROFILES: { html: true } });

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[45] px-4 sm:px-6 pb-4 sm:pb-6 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-5xl mx-auto pointer-events-auto rounded-3xl border border-border bg-white shadow-2xl shadow-neutral-900/10 backdrop-blur p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-5">
        {/* Icon + body */}
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="h-11 w-11 rounded-2xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center shrink-0">
            <Cookie className="h-5 w-5 text-amber-700" />
          </div>
          <div className="text-sm text-neutral-700 leading-relaxed flex-1 min-w-0 [&_a]:text-blue-600 [&_a]:underline [&_p]:my-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1">Cookies</div>
            <div
              data-testid="cookie-consent-banner-copy"
              dangerouslySetInnerHTML={{ __html: sanitizedCopy }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => decide("essentials")}
            className="px-5 py-2 rounded-full text-sm font-medium border border-border hover:border-neutral-300 hover:bg-neutral-50 transition"
            data-testid="cookie-consent-essentials"
          >
            Essentials only
          </button>
          <button
            type="button"
            onClick={() => decide("all")}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-br from-neutral-900 to-neutral-700 hover:from-neutral-800 text-white transition shadow-lg"
            data-testid="cookie-consent-accept-all"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => decide("dismissed")}
            className="h-8 w-8 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 flex items-center justify-center transition shrink-0"
            title="Dismiss for now"
            data-testid="cookie-consent-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
